import React from 'react';

import { CollapsibleCard, CollapsibleCardProps } from '@occc/ui-design-system';
import { useBuildCollapsibleCardParams } from '@occc-app/routes/Audience/hooks';
import { AttributeInfo } from '@occc-app/routes/Audience/AttributeInfo';
import {
  DynamicAudienceSection,
  TargetingAttributesFormField,
} from '@occc-app/routes/Audience/constants';

import { useSectionRefs } from '../context/AudienceSectionRefsContext';
import { useExpandedSections, useSectionState } from '../context/AudienceExpandedSectionsContext';

export type CollapsibleCardContainerProps = Omit<
  CollapsibleCardProps,
  'iconColor' | 'iconBackgroundColor' | 'rightSlot' | 'ref'
> & {
  fields: TargetingAttributesFormField[];
  sectionName: DynamicAudienceSection;
};

export const CollapsibleCardContainer = ({
  fields,
  children,
  sectionName,
  ...collapsibleCardProps
}: CollapsibleCardContainerProps) => {
  const buildCollapsibleCardParams = useBuildCollapsibleCardParams();
  const { formFieldUIStatus, ...uiParams } = buildCollapsibleCardParams(fields);
  const { setSectionRef } = useSectionRefs();
  const { onExpandedChange } = useExpandedSections();
  const isExpanded = useSectionState(sectionName);

  const ref = React.useCallback(
    (sectionElement: HTMLDivElement | null) => {
      setSectionRef(sectionName, sectionElement);
    },
    [sectionName, setSectionRef],
  );

  const onChange = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>, expanded: boolean) => {
      onExpandedChange(sectionName, expanded);
    },
    [onExpandedChange, sectionName],
  );

  return (
    <CollapsibleCard
      {...uiParams}
      {...collapsibleCardProps}
      rightSlot={<AttributeInfo formFieldUIStatus={formFieldUIStatus} />}
      expanded={isExpanded}
      onChange={onChange}
      ref={ref}
    >
      {children}
    </CollapsibleCard>
  );
};
